import {
  Badge,
  BlockStack,
  Card,
  InlineGrid,
  IndexTable,
  Modal,
  Spinner,
  Text,
} from "@shopify/polaris";
import React, { useState, useEffect } from "react";
import supabase from "../../../supabase-client";
import { useNavigate } from "react-router-dom";
import { formatFriendlyDate, formatPlate } from "../../../utility/funcs";

function InsuranceBar() {
  const navigate = useNavigate();
  const [insuranceData, setInsuranceData] = useState(undefined);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalData, setModalData] = useState(null);
  const [modalType, setModalType] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      const { data: overviewData } = await supabase.rpc(
        "get_insurance_status_overview"
      );
      setInsuranceData(overviewData[0]);
    };

    fetchData();
  }, []);

  const openModal = async (type) => {
    setModalType(type);
    setModalData(null);
    setModalOpen(true);

    if (type === "no_insurance") {
      setModalTitle("Autos Sin Seguro");
      const { data } = await supabase.rpc("get_cars_without_insurance");
      setModalData(data ?? []);
    } else if (type === "temporary") {
      setModalTitle("Autos con Solo Póliza Temporal");
      const { data } = await supabase.rpc("get_cars_with_temporary_insurance");
      setModalData(data ?? []);
    }
  };

  const modalContent = () => {
    if (!modalData) return <Spinner size="small" />;
    if (modalData.length === 0) return <Text>No hay resultados.</Text>;

    if (modalType === "no_insurance") {
      return (
        <IndexTable
          selectable={false}
          itemCount={modalData.length}
          headings={[
            { title: "Placa" },
            { title: "Modelo" },
          ]}
        >
          {modalData.map((car) => (
            <IndexTable.Row
              key={car.id}
              id={car.id}
              onClick={() => {
                setModalOpen(false);
                navigate(`/cars/${car.id}`);
              }}
            >
              <IndexTable.Cell>
                <Text fontWeight="semibold">{formatPlate(car.plate)}</Text>
              </IndexTable.Cell>
              <IndexTable.Cell>{car.show_as}</IndexTable.Cell>
            </IndexTable.Row>
          ))}
        </IndexTable>
      );
    }

    if (modalType === "temporary") {
      return (
        <IndexTable
          selectable={false}
          itemCount={modalData.length}
          headings={[
            { title: "Placa" },
            { title: "Modelo" },
            { title: "Expira" },
          ]}
        >
          {modalData.map((car) => (
            <IndexTable.Row
              key={car.id}
              id={car.id}
              onClick={() => {
                setModalOpen(false);
                navigate(`/cars/${car.id}`);
              }}
            >
              <IndexTable.Cell>
                <Text fontWeight="semibold">{formatPlate(car.plate)}</Text>
              </IndexTable.Cell>
              <IndexTable.Cell>
                {car.brand} {car.model}
              </IndexTable.Cell>
              <IndexTable.Cell>
                {formatFriendlyDate(car.expiry)}
              </IndexTable.Cell>
            </IndexTable.Row>
          ))}
        </IndexTable>
      );
    }
  };

  const policiesStatusBar = () => {
    const { temporary, no_insurance, expired } = insuranceData ?? {};
    return (
      <Card>
        <BlockStack gap={200}>
          <Text variant="headingSm">Pólizas</Text>
          {!insuranceData ? (
            <Spinner size="small" />
          ) : (
            <InlineGrid columns={3} gap={200}>
              <BlockStack gap={100}>
                <Text tone="subdued" variant="bodySm">
                  Sin Seguro
                </Text>
                {no_insurance > 0 ? (
                  <div
                    onClick={() => openModal("no_insurance")}
                    style={{ cursor: "pointer", display: "grid" }}
                  >
                    <Badge tone="critical-strong">
                      {no_insurance.toString()}
                    </Badge>
                  </div>
                ) : (
                  <Badge tone="subdued">{no_insurance.toString()}</Badge>
                )}
              </BlockStack>

              <BlockStack gap={100}>
                <Text tone="subdued" variant="bodySm">
                  Temporal
                </Text>
                {temporary > 0 ? (
                  <div
                    onClick={() => openModal("temporary")}
                    style={{ cursor: "pointer", display: "grid" }}
                  >
                    <Badge tone="attention">{temporary.toString()}</Badge>
                  </div>
                ) : (
                  <Badge>{temporary.toString()}</Badge>
                )}
              </BlockStack>

              <BlockStack gap={100}>
                <Text tone="subdued" variant="bodySm">
                  Expiradas
                </Text>
                {expired > 0 ? (
                  <div
                    onClick={() => navigate("/insurance/all?status=expired")}
                    style={{ cursor: "pointer", display: "grid" }}
                  >
                    <Badge tone="critical">{expired.toString()}</Badge>
                  </div>
                ) : (
                  <Badge>{expired.toString()}</Badge>
                )}
              </BlockStack>
            </InlineGrid>
          )}
        </BlockStack>
      </Card>
    );
  };

  return (
    <BlockStack gap={200}>
      <Text variant="headingMd">Pólizas de Seguros</Text>
      {policiesStatusBar()}

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={modalTitle}
      >
        <Modal.Section>{modalContent()}</Modal.Section>
      </Modal>
    </BlockStack>
  );
}

export default InsuranceBar;
